/**
 * Security utilities for frontend protection
 */

/**
 * Sanitizes user input to prevent XSS attacks.
 * Use this for any user-generated content that will be displayed.
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}

/**
 * Sanitizes HTML content, allowing only safe tags.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  
  // Remove potentially dangerous tags and attributes
  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
    /<embed\b[^<]*>/gi,
    /<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi,
    /on\w+\s*=/gi,
    /javascript:/gi,
    /data:/gi,
    /vbscript:/gi,
  ];
  
  let sanitized = html;
  for (const pattern of dangerousPatterns) {
    sanitized = sanitized.replace(pattern, '');
  }
  
  return sanitized;
}

/**
 * Validates email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates password strength
 */
export interface PasswordValidation {
  isValid: boolean;
  errors: string[];
}

export function validatePassword(password: string, minLength = 6): PasswordValidation {
  const errors: string[] = [];
  
  if (!password) {
    errors.push('كلمة المرور مطلوبة');
    return { isValid: false, errors };
  }
  
  if (password.length < minLength) {
    errors.push(`كلمة المرور يجب أن تكون ${minLength} أحرف على الأقل`);
  }
  
  // Additional checks for stronger passwords (optional)
  // if (!/[A-Z]/.test(password)) {
  //   errors.push('يجب أن تحتوي على حرف كبير');
  // }
  // if (!/[a-z]/.test(password)) {
  //   errors.push('يجب أن تحتوي على حرف صغير');
  // }
  // if (!/[0-9]/.test(password)) {
  //   errors.push('يجب أن تحتوي على رقم');
  // }
  
  return { isValid: errors.length === 0, errors };
}

/**
 * Generates a secure random token for CSRF protection
 */
export function generateCSRFToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Stores and retrieves CSRF token
 */
const CSRF_TOKEN_KEY = 'csrf_token';

export function getCSRFToken(): string {
  let token = sessionStorage.getItem(CSRF_TOKEN_KEY);
  if (!token) {
    token = generateCSRFToken();
    sessionStorage.setItem(CSRF_TOKEN_KEY, token);
  }
  return token;
}

export function clearCSRFToken(): void {
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
}

/**
 * Detects potential XSS in URLs
 */
export function isSafeUrl(url: string): boolean {
  if (!url) return false;
  
  const lowerUrl = url.toLowerCase().trim();
  
  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return false;
    }
  }
  
  return true;
}

/**
 * Safely opens external links
 */
export function openSafeLink(url: string): void {
  if (!isSafeUrl(url)) {
    console.warn('Blocked potentially dangerous URL:', url);
    return;
  }
  
  // Open in new tab with security attributes
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.click();
}

/**
 * Masks sensitive data for display
 */
export function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return email;
  
  const [name, domain] = email.split('@');
  const maskedName = name.length > 2 
    ? name[0] + '*'.repeat(name.length - 2) + name[name.length - 1]
    : '*'.repeat(name.length);
  
  return `${maskedName}@${domain}`;
}

/**
 * Prevents console access in production
 */
export function secureConsole(): void {
  if (import.meta.env.PROD) {
    // Disable console in production
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    // Keep console.warn and console.error for debugging critical issues
  }
}

/**
 * Detects dev tools opening (basic protection)
 */
export function detectDevTools(callback: () => void): void {
  const threshold = 160;
  
  const check = () => {
    if (
      window.outerWidth - window.innerWidth > threshold ||
      window.outerHeight - window.innerHeight > threshold
    ) {
      callback();
    }
  };
  
  setInterval(check, 1000);
}
